import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, useTheme } from '@mui/material';

/**
 * Enhanced AtomAnimation component displays an animated atomic structure
 * with electrons orbiting around a nucleus and connecting lines
 *
 * @param {Object} props Component props
 * @param {string} props.symbol Chemical symbol of the element
 * @param {number} props.electrons Number of electrons to display in the animation
 * @param {string} props.color Primary color for the animation
 * @param {number} props.size Size of the animation (default: 200)
 * @param {boolean} props.showDetails Whether to show additional details
 */
const AtomAnimation = ({
  symbol,
  electrons = 3,
  color = '#eab308',
  size = 200,
  showDetails = false,
  name = '',
  atomicNumber = ''
}) => {
  const theme = useTheme();
  const [electronPositions, setElectronPositions] = useState([]);

  // Calculate orbit paths based on number of electrons
  const orbits = [];
  const maxElectrons = Math.min(electrons, 12); // Allow more electrons for larger animations
  const nucleusSize = size / 5;

  // Create different orbit patterns based on electron count
  for (let i = 0; i < maxElectrons; i++) {
    // Distribute electrons in different shells based on count
    let shellIndex = 0;
    if (i >= 8) shellIndex = 2;
    else if (i >= 2) shellIndex = 1;

    const shellSize = (size / 2.5) * (0.6 + (shellIndex * 0.2)); // Different shell sizes
    const electronsInShell = shellIndex === 0 ? 2 : (shellIndex === 1 ? 6 : 4);
    const angleOffset = (shellIndex % 2) * (Math.PI / electronsInShell); // Offset angles for different shells

    const angle = angleOffset + ((i % electronsInShell) / electronsInShell) * Math.PI * 2;
    const delay = i * 0.1; // Stagger the animation start
    const duration = 5 - (shellIndex * 0.8) + (Math.random() * 2); // Vary the orbit duration
    const direction = i % 2 === 0 ? 1 : -1; // Alternate orbit directions

    orbits.push({
      angle,
      delay,
      duration,
      size: shellSize,
      direction,
      shellIndex,
      x: Math.cos(angle) * shellSize,
      y: Math.sin(angle) * shellSize
    });
  }

  // Update electron positions for connecting lines
  useEffect(() => {
    const interval = setInterval(() => {
      const newPositions = orbits.map((orbit, i) => {
        const time = (Date.now() / 1000) * (orbit.direction / orbit.duration);
        const angle = orbit.angle + (time * Math.PI * 2);
        return {
          x: Math.cos(angle) * orbit.size,
          y: Math.sin(angle) * orbit.size,
          shellIndex: orbit.shellIndex
        };
      });
      setElectronPositions(newPositions);
    }, 30); // Update at 30ms intervals for smooth animation

    return () => clearInterval(interval);
  }, []);

  // Generate a lighter shade of the color for the connecting lines
  const getLighterColor = (color, opacity = 0.4) => {
    return color + Math.floor(opacity * 255).toString(16).padStart(2, '0');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible',
        my: 2,
        mx: 'auto'
      }}
    >
      {/* Connecting Lines */}
      <svg
        width={size}
        height={size}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 5,
          overflow: 'visible'
        }}
      >
        {electronPositions.map((pos, index) => {
          // Different line styles for different shells
          const strokeWidth = 1.5 - (pos.shellIndex * 0.3);
          const strokeDasharray = pos.shellIndex === 0 ? "" : (pos.shellIndex === 1 ? "3,2" : "1,2");
          const centerX = size / 2;
          const centerY = size / 2;

          return (
            <motion.line
              key={index}
              x1={centerX}
              y1={centerY}
              x2={centerX + pos.x}
              y2={centerY + pos.y}
              stroke={getLighterColor(color)}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 1, delay: index * 0.1 }}
            />
          );
        })}
      </svg>

      {/* Orbit Paths */}
      {orbits.map((orbit, index) => (
        <motion.div
          key={`orbit-${index}`}
          initial={{ opacity: 0, rotate: 0 }}
          animate={{
            opacity: 0.2 + (orbit.shellIndex * 0.1),
            rotate: orbit.direction > 0 ? 360 : -360
          }}
          transition={{
            opacity: { duration: 1, delay: orbit.delay },
            rotate: { duration: 60, repeat: Infinity, ease: "linear" }
          }}
          style={{
            position: 'absolute',
            width: orbit.size * 2,
            height: orbit.size * 2,
            borderRadius: '50%',
            border: `${1 + (orbit.shellIndex * 0.5)}px ${orbit.shellIndex === 0 ? 'solid' : 'dashed'} ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}`,
            zIndex: 1
          }}
        />
      ))}

      {/* Nucleus */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.8, type: 'spring' }}
        style={{
          position: 'absolute',
          width: nucleusSize,
          height: nucleusSize,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10,
          boxShadow: `0 0 ${nucleusSize/2}px ${color}`
        }}
      >
        <Typography
          variant={nucleusSize > 40 ? "h6" : "subtitle1"}
          sx={{
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0 0 3px rgba(0,0,0,0.5)'
          }}
        >
          {symbol}
        </Typography>
      </motion.div>

      {/* Electrons */}
      {orbits.map((orbit, index) => {
        const electronSize = 6 + (orbit.shellIndex * 2);
        const centerX = size / 2;
        const centerY = size / 2;
        const position = electronPositions[index] || { x: orbit.x, y: orbit.y };

        return (
          <motion.div
            key={`electron-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: orbit.delay }}
            style={{
              position: 'absolute',
              width: electronSize,
              height: electronSize,
              borderRadius: '50%',
              backgroundColor: color,
              boxShadow: `0 0 ${electronSize/2}px ${color}`,
              zIndex: 15,
              left: centerX + position.x - (electronSize/2),
              top: centerY + position.y - (electronSize/2)
            }}
          />
        );
      })}

      {/* Element Details (optional) */}
      {showDetails && (
        <Box sx={{
          position: 'absolute',
          bottom: -40,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 20
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {name} ({symbol})
          </Typography>
          {atomicNumber && (
            <Typography variant="caption" color="text.secondary">
              Atomic #: {atomicNumber}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AtomAnimation;
